import React, { useEffect, useState } from 'react';
import { useFetchOrdersDeliveredAtForReport } from 'hooks/useFetchOrdersDeliveredAtForReport';
import { useFetchShiftByRegion } from 'hooks/useFetchShift';
import Input from 'components/inputs/Input';
import Dropdown from 'components/dropdown/Dropdown';
import {
  CalendarFetchByPeriods,
  getEndDate,
  getStartDate,
} from 'components/OrderListTable/CalendarFetch/CalendarFetchByPeriods';
import { ReqEnum } from 'const/requestStatus';
import './MyDeliveriesReportComposition.scss';
import { CourierInvoice } from '../../types/reportTypes';
import {
  errorCourierInvoiceObjReplacment,
  ShiftInvoiceDataToReplace,
} from './InvoiceMultipleCourierReportComposition';
import {
  calcInvoiceSummary,
  calcSumDeviationAvgCompensation,
  CompanyInfoInvoiceBox,
  CompensationInfoBox,
  getInvoiceSummaryData,
  InvoiceHeader,
  InvoiceTable,
  PeriodCompensationInfoBox,
  useFetchIsMva,
} from '../../components/OrderListTable/BillingCourier/BillingCourier';
import { Loader } from '../../components/Loader/Loader';
import dayjs from 'dayjs';
import { reqStatus, Region } from '../../types/orderTypes';
import {
  ShiftCourierJson,
  ShiftCourierWithDateJson,
  ShiftStatusType,
} from '../../const/ShiftConst';
import {
  ExtraWorkCompensation,
  PerDayInvoice,
} from '../../components/OrderListTable/utils/arrayManipulation2';
import { prodCostTableForCourierWithShiftData } from '../../components/OrderListTable/utils/prodCostTableForCourierWithShiftData';
import { CourierTravelModeString } from '../../types/bundleTypes';
import { getOrgNumberAsNumber } from '../../utils/enhetsregisteretUtil';
import { flatten } from '../../utils/array';
import { ButtonComponent } from '../../ReusableComponents/Button/ButtonComponent';
import { Helmet } from 'react-helmet';

declare interface CompanyNameDropdown {
  courierId: number;
  fullName: string;
  companyNumber: number;
  companyName: string;
}

function isCompanyNameInCompanyNameList(
  companyNameArray: CompanyNameDropdown[],
  selectedCompanyName: string
): boolean {
  const arrayMatchingSelectedCourierName = companyNameArray
    .map(item => item.companyName)
    .filter(item => item !== null)
    .filter(obj => obj.indexOf(selectedCompanyName) === 0);

  return arrayMatchingSelectedCourierName.length ? true : false;
}

function useOutsideOsloCompensationModel(region): boolean {
  return [Region.STAVANGER, Region.TRONDHEIM, Region.BERGEN].includes(region);
}

export function findUniqueCompanyNameListFromCompanyNameList(
  companyNameArray: CompanyNameDropdown[],
  searchText: string
): CompanyNameDropdown[] {
  if (companyNameArray) {
    const filteredObject = companyNameArray
      .filter(obj => obj.companyName !== null)
      .filter(obj => obj.companyName !== 'null')
      .filter(obj =>
        obj.companyName.toLowerCase().includes(searchText.toLowerCase())
      )
      .sort((a, b) => a.companyName.localeCompare(b.companyName));

    return filteredObject;
  } else {
    return [
      {
        courierId: 0,
        companyName: 'loading...',
        companyNumber: 0,
        fullName: 'loading...',
      },
    ];
  }
}

export function makeInvoiceListBySelectedCompany(
  courierInvoiceList: CourierInvoice[],
  companyNameArray: CompanyNameDropdown[],
  companyName: string
): CourierInvoice[] {
  if (companyName.length > 0 && companyNameArray && companyNameArray.length) {
    const showInvoiceBySelectedCourier: CourierInvoice[] =
      courierInvoiceList.filter(obj => obj.courierCompanyName === companyName);

    const courierIdForChosenName: number = companyNameArray.filter(
      obj => obj.companyName === companyName
    )[0].courierId;

    const companyNrForCompanyName: number = companyNameArray.filter(
      obj => obj.companyName === companyName
    )[0].companyNumber;

    if (showInvoiceBySelectedCourier.length === 0) {
      const courierInvoiceObjReplacment: CourierInvoice[] = [
        {
          counts: [],
          courierCompanyName: companyName,
          courierCompanyNumber: companyNrForCompanyName,
          courierFirstName: 'unknown',
          courierId: Number(courierIdForChosenName),
          courierLastName: '',
          courierTravelMode: ShiftInvoiceDataToReplace.courierTravelMode,
          courierAvgRatingForDateRange: 0,
        },
      ];

      return courierInvoiceObjReplacment;
    }

    return showInvoiceBySelectedCourier;
  } else {
    return [errorCourierInvoiceObjReplacment];
  }
}

export function InvoiceCourierForCompanyList({
  courierInvoiceForSelectedCourier,
  courierListBookedFetched,
  fetchStatusShift,
  region,
}: {
  courierInvoiceForSelectedCourier: CourierInvoice;
  courierListBookedFetched: ShiftCourierWithDateJson[];
  fetchStatusShift: reqStatus;
  region: Region;
}): JSX.Element {
  const courierTravelMode = courierInvoiceForSelectedCourier
    ? courierInvoiceForSelectedCourier.courierTravelMode
    : CourierTravelModeString.CAR.toString();
  const useOutsideOsloCompensation = [
    Region.STAVANGER,
    Region.BERGEN,
    Region.TRONDHEIM,
  ].includes(region);

  const ordersPerDay: PerDayInvoice[] = prodCostTableForCourierWithShiftData(
    courierInvoiceForSelectedCourier,
    courierTravelMode,
    courierListBookedFetched,
    fetchStatusShift,
    useOutsideOsloCompensation
  );
  return (
    <>
      {fetchStatusShift === ReqEnum.SUCCESS && ordersPerDay.length ? (
        <SummaryInvoiceForCompanyList
          ordersPerDay={ordersPerDay}
          courierInvoiceForSelectedCourier={courierInvoiceForSelectedCourier}
          region={region}
        />
      ) : (
        <Loader />
      )}
    </>
  );
}

export function SummaryInvoiceForCompanyList({
  ordersPerDay,
  courierInvoiceForSelectedCourier,
  region,
}: {
  ordersPerDay: PerDayInvoice[];
  courierInvoiceForSelectedCourier: CourierInvoice;
  region: Region;
}): JSX.Element {
  const isOutsideOsloSelected = useOutsideOsloCompensationModel(region);
  const {
    totalNrOforders,
    sumCompensationForShifts,
    SumCompensation,
    nrOfDayShifts,
    compForDeliveries,
    extraWorkCompensation,
    minWageSummary,
    minWageDays,
  }: SummaryInvoiceData = calcInvoiceSummary(
    ordersPerDay,
    isOutsideOsloSelected
  );

  const travelModeForCourier =
    courierInvoiceForSelectedCourier.courierTravelMode;

  const deviationAvgCompensationToShow = isOutsideOsloSelected
    ? 0
    : calcSumDeviationAvgCompensation(totalNrOforders, travelModeForCourier);

  const { courierName, orgNummer } = getInvoiceSummaryData(
    courierInvoiceForSelectedCourier
  );
  const { isMva, fetchIsMvaStatus, vatForCouriers } = useFetchIsMva(orgNummer);
  return (
    <>
      <div className="SummaryInvoice summaryInvoiceForCompanyList">
        <CompanyInfoInvoiceBox
          courierInvoiceForSelectedCourier={courierInvoiceForSelectedCourier}
          courierName={courierName}
          orgNummer={orgNummer}
          fetchIsMvaStatus={fetchIsMvaStatus}
          isMva={isMva}
        />

        <PeriodCompensationInfoBox
          nrOfDayShifts={nrOfDayShifts}
          sumCompensationForShifts={sumCompensationForShifts}
          totalNrOforders={totalNrOforders}
          compForDeliveries={compForDeliveries}
          SumCompensation={SumCompensation}
          vatForCouriers={vatForCouriers}
          deviationComp={deviationAvgCompensationToShow}
          extraWorkCompensation={extraWorkCompensation}
          courierAvgRatingForDateRange={
            courierInvoiceForSelectedCourier.courierAvgRatingForDateRange
          }
          region={region}
          minWageDays={minWageDays}
          minWageSummary={minWageSummary}
        />

        <CompensationInfoBox
          courierInvoiceForSelectedCourier={courierInvoiceForSelectedCourier}
          region={region}
        />
      </div>
      <div className="positionBoWideItem invoiceTable">
        <InvoiceTable ordersPerDay={ordersPerDay} region={region} />
      </div>
    </>
  );
}

function CompanyInfoInvoiceSummaryBox({
  companyName,
  orgNummer,
  fetchIsMvaStatus,
  isMva,
}) {
  return (
    <div className="positionBox companyInfoInvoiceSummaryBox">
      <div className="textBox">
        <div className="title"> Company</div>
        <div className="bigTextBlock">{companyName}</div>

        <div className="sText">
          <div>Org. number:</div>
          <div>{orgNummer}</div>
        </div>

        <div className="sText">
          <div>VAT registered</div>
          <div>
            {fetchIsMvaStatus !== ReqEnum.SUCCESS ? (
              <Loader />
            ) : isMva ? (
              'Yes'
            ) : (
              'No'
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export interface SummaryInvoiceData {
  totalNrOforders: number;
  sumCompensationForShifts: number;
  SumCompensation: number;
  nrOfDayShifts: number;
  compForDeliveries: number;
  extraWorkCompensation: ExtraWorkCompensation;
  minWageDays?: number;
  minWageSummary?: number;
}

function calcInvoiceSummaryForCompanyList(
  ordersPerCourierPerDay: PerDayInvoice[][],
  useOutsideOsloCompensationModel: boolean
): SummaryInvoiceData {
  const sumInvoicePerCourier: SummaryInvoiceData[] = ordersPerCourierPerDay.map(
    (ordersPerDay: PerDayInvoice[], index: number) => {
      const invoiceDataPerDay: SummaryInvoiceData = calcInvoiceSummary(
        ordersPerDay,
        useOutsideOsloCompensationModel
      );

      return {
        totalNrOforders: invoiceDataPerDay.totalNrOforders,
        sumCompensationForShifts: invoiceDataPerDay.sumCompensationForShifts,
        SumCompensation: invoiceDataPerDay.SumCompensation,
        nrOfDayShifts: invoiceDataPerDay.nrOfDayShifts,
        compForDeliveries: invoiceDataPerDay.compForDeliveries,
        extraWorkCompensation: {
          allExtraWorkCompensation:
            invoiceDataPerDay.extraWorkCompensation.allExtraWorkCompensation,
          sortWorkCompensation:
            invoiceDataPerDay.extraWorkCompensation.sortWorkCompensation,
          inboundWorkCompensation:
            invoiceDataPerDay.extraWorkCompensation.inboundWorkCompensation,
          backupWorkCompensation:
            invoiceDataPerDay.extraWorkCompensation.backupWorkCompensation,
          otherWorkCompensation:
            invoiceDataPerDay.extraWorkCompensation.otherWorkCompensation,
        },
        minWageSummary: invoiceDataPerDay.minWageSummary,
        minWageDays: invoiceDataPerDay.minWageDays,
      };
    }
  );

  const totalInvoice: SummaryInvoiceData = sumInvoicePerCourier.reduce(
    (acc, cur) => {
      const newObj = {
        totalNrOforders: acc.totalNrOforders + cur.totalNrOforders,
        sumCompensationForShifts:
          acc.sumCompensationForShifts + cur.sumCompensationForShifts,
        SumCompensation: acc.SumCompensation + cur.SumCompensation,
        nrOfDayShifts: acc.nrOfDayShifts + cur.nrOfDayShifts,
        compForDeliveries: acc.compForDeliveries + cur.compForDeliveries,
        extraWorkCompensation: {
          allExtraWorkCompensation:
            acc.extraWorkCompensation.allExtraWorkCompensation +
            cur.extraWorkCompensation.allExtraWorkCompensation,
          sortWorkCompensation:
            acc.extraWorkCompensation.sortWorkCompensation +
            cur.extraWorkCompensation.sortWorkCompensation,
          inboundWorkCompensation:
            acc.extraWorkCompensation.inboundWorkCompensation +
            cur.extraWorkCompensation.inboundWorkCompensation,
          backupWorkCompensation:
            acc.extraWorkCompensation.backupWorkCompensation +
            cur.extraWorkCompensation.backupWorkCompensation,
          otherWorkCompensation:
            acc.extraWorkCompensation.otherWorkCompensation +
            cur.extraWorkCompensation.otherWorkCompensation,
        },
        minWageDays: acc.minWageDays + cur.minWageDays,
        minWageSummary: acc.minWageSummary + cur.minWageSummary,
      };

      return newObj;
    }
  );

  return totalInvoice;
}

function InvoiceHeaderSummarySeparator({ titleText = 'Summary by courier' }) {
  return (
    <div className="separatingSummary">
      <div className="SummaryInvoiceTitle">{'Summary by courier'}</div>
    </div>
  );
}

function CompanyInvoiceSummary({
  invoiceList,
  courierListBookedFetched,
  fetchStatusShift,
  region,
}: {
  invoiceList: CourierInvoice[];
  courierListBookedFetched: ShiftCourierWithDateJson[];
  fetchStatusShift: reqStatus;
  region: Region;
}) {
  const companyName = invoiceList[0].courierCompanyName
    ? invoiceList[0].courierCompanyName
    : 'Not registered';
  const useOutsideOsloCompensation = [
    Region.STAVANGER,
    Region.BERGEN,
    Region.TRONDHEIM,
  ].includes(region);

  const ordersPerDay: PerDayInvoice[][] = invoiceList.map(invoice =>
    prodCostTableForCourierWithShiftData(
      invoice,
      invoice
        ? invoice.courierTravelMode
        : CourierTravelModeString.CAR.toString(),
      courierListBookedFetched,
      fetchStatusShift,
      useOutsideOsloCompensation
    )
  );
  const {
    totalNrOforders,
    sumCompensationForShifts,
    SumCompensation,
    nrOfDayShifts,
    compForDeliveries,
    minWageDays,
    minWageSummary,
    extraWorkCompensation,
  }: SummaryInvoiceData = calcInvoiceSummaryForCompanyList(
    ordersPerDay,
    useOutsideOsloCompensation
  );

  const travelModeForCourierCompany = invoiceList[0].courierTravelMode;

  const deviationAvgCompensation = useOutsideOsloCompensation
    ? 0
    : calcSumDeviationAvgCompensation(
        totalNrOforders,
        travelModeForCourierCompany
      );

  const orgNummer = getInvoiceSummaryData(invoiceList[0]).orgNummer;
  const { isMva, fetchIsMvaStatus, vatForCouriers } = useFetchIsMva(orgNummer);

  return (
    <>
      <div
        className="SummaryInvoice companyInvoiceSummary"
        style={{ height: '100%' }}
      >
        <CompanyInfoInvoiceSummaryBox
          companyName={companyName}
          orgNummer={orgNummer}
          fetchIsMvaStatus={fetchIsMvaStatus}
          isMva={isMva}
        />

        <PeriodCompensationInfoBox
          nrOfDayShifts={nrOfDayShifts}
          sumCompensationForShifts={sumCompensationForShifts}
          totalNrOforders={totalNrOforders}
          compForDeliveries={compForDeliveries}
          SumCompensation={SumCompensation}
          vatForCouriers={vatForCouriers}
          deviationComp={deviationAvgCompensation}
          extraWorkCompensation={extraWorkCompensation}
          region={region}
          minWageSummary={minWageSummary}
          minWageDays={minWageDays}
        />
      </div>
    </>
  );
}

function BillingCourierByCompanyNumber({
  fetchStatus,
  showInvoiceBySelectedCourier,
  selectedCourierId,
  startDate,
  endDate,
  courierListBookedFetched,
  fetchStatusShift,
  region,
}: {
  fetchStatus: reqStatus;
  showInvoiceBySelectedCourier: CourierInvoice[];
  selectedCourierId: number;
  startDate: dayjs.Dayjs;
  endDate: dayjs.Dayjs;
  courierListBookedFetched: ShiftCourierWithDateJson[];
  fetchStatusShift: reqStatus;
  region: Region;
}) {
  return (
    <>
      {fetchStatus === ReqEnum.PENDING && (
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <Loader type="spinner" size="large" />
        </div>
      )}

      {selectedCourierId === null ? (
        <div>
          {' '}
          This courier dont have a courier Id. Talk to the programmers or add it
          yourself
        </div>
      ) : (
        <>
          <div
            className={
              (fetchStatus || fetchStatusShift) === ReqEnum.PENDING
                ? 'orderlisttable is-loading'
                : 'orderlisttable'
            }
          >
            <div className="orderlisttable__header">
              <div className="orderlisttable__header__buttons u-screen-only">
                <ButtonComponent
                  onClick={() => window.print()}
                  disabled={showInvoiceBySelectedCourier === undefined}
                >
                  Download PDF
                </ButtonComponent>
              </div>
            </div>
            <InvoiceHeader
              startDate={startDate}
              endDate={endDate}
              titleText={'Company period report'}
            />

            <CompanyInvoiceSummary
              invoiceList={showInvoiceBySelectedCourier}
              courierListBookedFetched={courierListBookedFetched}
              fetchStatusShift={fetchStatusShift}
              region={region}
            />

            <InvoiceHeaderSummarySeparator />

            <div>
              {showInvoiceBySelectedCourier.map(
                (courierInvoice: CourierInvoice, index: number) => {
                  return (
                    <div key={index}>
                      {courierInvoice === undefined &&
                      courierListBookedFetched === undefined ? (
                        <div style={{ marginLeft: '5px' }}> No Records </div>
                      ) : (
                        <>
                          <InvoiceCourierForCompanyList
                            courierInvoiceForSelectedCourier={courierInvoice}
                            courierListBookedFetched={courierListBookedFetched}
                            fetchStatusShift={fetchStatusShift}
                            region={region}
                          />
                        </>
                      )}
                    </div>
                  );
                }
              )}
            </div>
          </div>
        </>
      )}
    </>
  );
}

function useShowInvoiceBySelectedCourierCompany(
  courierInvoiceList: CourierInvoice[],
  selectedCompanyName: string,
  fetchStatus: reqStatus,
  searchText: string,
  courierListBookedFetched: ShiftCourierWithDateJson[],
  fetchStatusShift: reqStatus,
  setSelectedCompanyName
): {
  selectedCourierId: number[];
  showInvoiceBySelectedCourier: CourierInvoice[];
  uniqueCourierNameListObject: CompanyNameDropdown[];
} {
  const [selectedCourierId, setSelectedCourierId] = useState<number[]>([0]);

  const [showInvoiceBySelectedCourier, setShowInvoiceBySelectedCourier] =
    useState<CourierInvoice[] | undefined>();

  const [uniqueCourierNameListObject, setUniqueCourierNameListObject] =
    useState<CompanyNameDropdown[]>();

  useEffect(() => {
    const updateData = (
      courierInvoiceList: CourierInvoice[],
      selectedCompanyName: string,
      fetchStatus: reqStatus,
      searchText: string,
      courierListBookedFetched: ShiftCourierWithDateJson[],
      fetchStatusShift: reqStatus,
      setSelectedCompanyName
    ) => {
      if (
        fetchStatus === ReqEnum.SUCCESS &&
        fetchStatusShift === ReqEnum.SUCCESS
      ) {
        const courierNameArrayByOrders: CompanyNameDropdown[] =
          courierInvoiceList.map((item: CourierInvoice) => {
            const fullname = `${item.courierFirstName} ${item.courierLastName}`;
            return {
              courierId: item.courierId,
              fullName: fullname,
              companyNumber: getOrgNumberAsNumber(item.courierCompanyNumber),
              companyName: item.courierCompanyName
                ? item.courierCompanyName
                : 'unknown',
            };
          });
        const courierNameArrayByShifts: CompanyNameDropdown[] = flatten(
          courierListBookedFetched.map(
            (obj1: ShiftCourierWithDateJson) => obj1.courierDayBooked
          )
        )
          .filter(
            (obj: ShiftCourierJson) => obj.status === ShiftStatusType.BOOKED
          )
          .map((item: ShiftCourierJson) => {
            const thereIsNoCompanyNumberOnShifts = null;
            const thereIsNoCompanyNameOnShifts = null;
            return {
              courierId: item.courierId,
              fullName: item.name,
              companyNumber: thereIsNoCompanyNumberOnShifts,
              companyName: thereIsNoCompanyNameOnShifts,
            };
          });

        const courierNameArrayWithDuplicates = [
          ...courierNameArrayByOrders,
          ...courierNameArrayByShifts,
        ];
        const companyNameArray: CompanyNameDropdown[] =
          courierNameArrayWithDuplicates
            .map(obj => obj.companyNumber)
            .reduce(
              (unique, item) =>
                unique.includes(item) ? unique : [...unique, item],
              []
            )
            .map((item: number) => ({
              companyNumber: item,
              companyName: courierNameArrayWithDuplicates.find(
                (obj, ix) => obj.companyNumber === item
              ).companyName,
              courierId: courierNameArrayWithDuplicates.find(
                obj => obj.companyNumber === item
              ).courierId,
              fullName: courierNameArrayWithDuplicates.find(
                obj => obj.companyNumber === item
              ).fullName,
            }));

        setUniqueCourierNameListObject(
          findUniqueCompanyNameListFromCompanyNameList(
            companyNameArray,
            searchText
          )
        );
        if (
          isCompanyNameInCompanyNameList(companyNameArray, selectedCompanyName)
        ) {
          const showInvoiceBySelectedCourier: CourierInvoice[] =
            makeInvoiceListBySelectedCompany(
              courierInvoiceList,
              companyNameArray,
              selectedCompanyName
            );

          setShowInvoiceBySelectedCourier(showInvoiceBySelectedCourier);

          // if (showInvoiceBySelectedCourier.courierId) {
          //   setSelectedCourierId(showInvoiceBySelectedCourier.courierId);
          // } else {
          //   setSelectedCourierId(null);
          // }
          setSelectedCourierId(
            showInvoiceBySelectedCourier.map(invoice => invoice.courierId)
          );
        } else {
          setSelectedCourierId([0]);
          setSelectedCompanyName('');
        }
      }
    };
    updateData(
      courierInvoiceList,
      selectedCompanyName,
      fetchStatus,
      searchText,
      courierListBookedFetched,
      fetchStatusShift,
      setSelectedCompanyName
    );
  }, [
    courierInvoiceList,
    courierListBookedFetched,
    fetchStatus,
    fetchStatusShift,
    searchText,
    selectedCompanyName,
    setSelectedCompanyName,
  ]);

  return {
    selectedCourierId: selectedCourierId,
    showInvoiceBySelectedCourier: showInvoiceBySelectedCourier,
    uniqueCourierNameListObject: uniqueCourierNameListObject,
  };
}

export function InvoiceCourierCompanyReportComposition({
  region,
}: {
  region: Region;
}): JSX.Element {
  const [startDate, setStartDate] = useState(getStartDate());
  const [endDate, setEndDate] = useState(getEndDate());
  const [searchText, setSearchText] = useState<string>('');

  const [courierInvoiceList, fetchStatus] = useFetchOrdersDeliveredAtForReport(
    startDate,
    endDate,
    region
  );

  const [courierListBookedFetched, fetchStatusShift] = useFetchShiftByRegion(
    startDate,
    endDate,
    region
  );

  const [selectedCompanyName, setSelectedCompanyName] = useState<string>('');

  const { showInvoiceBySelectedCourier, uniqueCourierNameListObject } =
    useShowInvoiceBySelectedCourierCompany(
      courierInvoiceList,
      selectedCompanyName,
      fetchStatus,
      searchText,
      courierListBookedFetched,
      fetchStatusShift,
      setSelectedCompanyName
    );

  const fetchValues = values => {
    const { fetchStartDate, fetchEndDate } = values;
    setStartDate(fetchStartDate);
    setEndDate(fetchEndDate);
  };

  const chooseCourier = courierName => {
    setSelectedCompanyName(courierName);
  };

  const listOfNames: string[] = uniqueCourierNameListObject
    ? (uniqueCourierNameListObject as any).length > 0
      ? (uniqueCourierNameListObject as any).map(item => item.companyName)
      : ['Empty for this period']
    : ['Loading ...'];

  return (
    <>
      <Helmet>
        <title>Reports - Invoice Company</title>
      </Helmet>
      <div className="chooseCourierInvoiceRow">
        <Input
          className="searchInput"
          placeholder="Filter company list.."
          onChange={event => setSearchText(event.target.value)}
          value={searchText}
          autoFocus={searchText !== ''}
          onRef={input => searchText !== '' && input && input.focus()}
          disabled={fetchStatus !== ReqEnum.SUCCESS}
        />
        <Dropdown
          placeholder={
            selectedCompanyName
              ? selectedCompanyName
              : 'Show invoice by company'
          }
          onChange={courierName => chooseCourier(courierName)}
          names={listOfNames}
        />
      </div>

      <CalendarFetchByPeriods
        fetchValues={fetchValues}
        givenStartDate={startDate}
        givenEndDate={endDate}
      />
      {selectedCompanyName.length > 0 && (
        <BillingCourierByCompanyNumber
          selectedCourierId={0 /*selectedCourierId as number*/}
          fetchStatus={fetchStatus}
          showInvoiceBySelectedCourier={
            showInvoiceBySelectedCourier as CourierInvoice[]
          }
          startDate={startDate}
          endDate={endDate}
          courierListBookedFetched={courierListBookedFetched}
          fetchStatusShift={fetchStatusShift}
          region={region}
        />
      )}
    </>
  );
}
